import { Check, Delete, DoneAll } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Container,
    darken,
    Divider,
    IconButton,
    MenuItem,
    Pagination,
    Select,
    SelectChangeEvent,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, ColumnSort, OnChangeFn, SortingState, VisibilityState } from '@tanstack/react-table';
import deepEqual from 'fast-deep-equal';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { DocumentType, SavedFilterType } from '../../api/dto';
import { GetManyPaged, GetManyPagedWithParam } from '../../api/types';
import { TFilterElement, TFormElement } from '../../forms/types';
import { UiSchemaType } from '../../forms/UiSchemaType';
import { useDeviceType } from '../../hooks/useDeviceType';
import { useGridTableLocalStorageState } from '../../hooks/useGridTableLocalStorageState';
import { useImpactTable } from '../../hooks/useImpactTable';
import { Nullable } from '../../lib/types';
import { getRoute } from '../../routes/routes';
import { ImpactHelmet } from '../ImpactHelmet';
import { ResponsiveButton } from '../ResponsiveButton';
import { ImpactTable } from '../tables/table/ImpactTable';
import { SearchFilter, SearchFilterTyped, toSpringFilterQuery } from './filters';
import { FiltersDialog } from './FiltersDialog';
import { SearchCardList } from './SearchCardList';
import { SearchPageHeader } from './SearchPageHeader';
import { SearchView } from './SearchView';
import { SimpleFiltersDialog } from './SimpleFiltersDialog';
import { sanitizeFiltersBySchema } from './utils';

export interface SearchFieldValues {
    filters?: Nullable<SearchFilter>[];
}

export interface BaseData {
    id: number | string;
}

export interface BulkActionsConfig<TBulkAction extends string = string> {
    options: TBulkAction[];
    translations: Record<TBulkAction, MessageDescriptor['id']>;
    page: string;
}

export interface BulkActionsConfirmationConfig<TData extends BaseData> {
    onConfirm: (selectedRows: TData[]) => void;
    action: string;
    selected: number;
    total: number;
    searchPage: string;
    disabled: boolean;
}

export interface SearchPageProps<
    TData extends BaseData,
    TBulkAction extends string = string,
    TParam extends object = object
> {
    pageKey: string;
    documentType: DocumentType | null;
    title: string;
    columns: ColumnDef<TData, any>[];
    filterDialogTitle?: string;
    filterSchemas: TFilterElement[];
    initialFilters?: SearchFilter[];
    baseFilters?: SearchFilter[];
    pagedApiCall?: GetManyPaged<TData>;
    pagedApiCallWithParam?: {
        method: GetManyPagedWithParam<TParam, TData>;
        param: TParam;
    };
    CardItem?: ({
        item,
        handleExpandToggle,
        isExpanded,
    }: {
        item: TData;
        handleExpandToggle: (item: TData) => void;
        isExpanded: (item: TData) => boolean;
    }) => ReactElement;
    initialSort?: ColumnSort;
    savedFilterType?: SavedFilterType;
    selectable?: boolean;
    embedded?: boolean;
    selectedRows?: TData[];
    onSelect?: (name: any[]) => void;
    getRowId?: (row: TData) => number;
    bulkActions?: BulkActionsConfig<TBulkAction>;
    bulkActionsConfirmation?: BulkActionsConfirmationConfig<TData>;
    hideDefaultHeader?: boolean;
    header?: React.ReactNode;
    predefinedFilters?: {
        filters: SearchFilter[];
        labelKey: MessageDescriptor['id'];
        tooltipKey?: MessageDescriptor['id'];
        sorting?: SortingState;
    }[];
}

const PAGE_SIZE_OPTIONS = [20, 30, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

export const SearchPage = <
    TData extends BaseData,
    TBulkAction extends string = string,
    TParam extends object = object
>({
    pageKey,
    documentType,
    title,
    columns,
    filterSchemas,
    filterDialogTitle,
    initialFilters,
    baseFilters,
    pagedApiCall,
    pagedApiCallWithParam,
    CardItem,
    initialSort,
    savedFilterType,
    selectable = false,
    embedded = false,
    selectedRows,
    onSelect,
    getRowId,
    bulkActions,
    bulkActionsConfirmation,
    hideDefaultHeader,
    header,
    predefinedFilters,
}: SearchPageProps<TData, TBulkAction, TParam>): ReactElement => {
    const intl = useIntl();

    const sanitizeFilters = useCallback(
        (filters: Nullable<SearchFilter>[] | undefined | null) => sanitizeFiltersBySchema(filters, filterSchemas),
        [filterSchemas]
    );
    const navigate = useNavigate();
    const location = useLocation();
    const [localStorageState, setLocalStorageState] = useGridTableLocalStorageState(pageKey);
    const { isMobile } = useDeviceType();
    // Mobile devices are shown the list view by default
    const [view, setView] = useState<SearchView>(() =>
        location.state?.view
            ? location.state?.view
            : isMobile && CardItem !== undefined
            ? SearchView.LIST
            : SearchView.TABLE
    );
    const [filtering, setFiltering] = useState(false);
    const [expandAll, setExpandAll] = useState(false);
    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
    const [page, setPage] = React.useState(() => location.state?.page || 1);
    const [pageSize, setPageSize] = React.useState(() => location.state?.pageSize || DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = React.useState(0);
    const [totalElements, setTotalElements] = React.useState(0);
    const [filters, setFilters] = useState<SearchFilterTyped[]>(() =>
        sanitizeFilters(location.state?.filters ? location.state.filters : initialFilters)
    );
    const [showBulkAction, setShowBulkAction] = useState<boolean>(() => location.state?.showBulkAction ?? false);
    const [selectedBulkAction, setSelectedBulkAction] = useState<string>('');

    const selectedCount = selectedRows?.length;
    const onClearSelection = onSelect !== undefined ? () => onSelect([]) : undefined;

    const exitBulkAction = () => {
        setShowBulkAction(false);
        setSelectedBulkAction('');
        onSelect?.([]);
        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: { ...location.state, selectedIds: undefined, showBulkAction: false },
        });
    };

    const selectBulkAction = (bulkAction: string) => {
        if (bulkActions) {
            setSelectedBulkAction(bulkAction);
            navigate(
                getRoute({
                    path: bulkActions.page,
                    params: { action: bulkAction },
                }),
                {
                    state: {
                        ...location.state,
                        page: 1,
                        selectedIds: (selectedRows ?? []).map((row) => (getRowId ? getRowId(row) : row.id)),
                    },
                }
            );
        }
    };

    const cancelBulkAction = () => {
        if (bulkActionsConfirmation) {
            navigate(
                getRoute({
                    path: bulkActionsConfirmation.searchPage,
                }),
                {
                    state: {
                        ...location.state,
                        page: 1,
                        selectedIds: (selectedRows ?? []).map((row) => (getRowId ? getRowId(row) : row.id)),
                        showBulkAction: true,
                    },
                }
            );
        }
    };

    const resolveSorting = (locationSorting?: SortingState, fallback?: ColumnSort): SortingState =>
        locationSorting?.length ? locationSorting : fallback ? [fallback] : [];

    const [sorting, setSorting] = useState<SortingState>(() => resolveSorting(location.state?.sorting, initialSort));

    useEffect(() => {
        setSorting(resolveSorting(location.state?.sorting, initialSort));
    }, [location.state?.sorting]);

    const defaultColumnVisibilities = useMemo(
        () =>
            Object.fromEntries(
                columns
                    .slice()
                    .sort((a, b) => (a.id ?? '').localeCompare(b.id ?? ''))
                    .map((column) => [column.id ?? '', !column.meta?.initiallyHidden])
            ),
        [columns]
    );
    const [columnVisibilities, setColumnVisibilities] = useState<VisibilityState>(
        Object.fromEntries(
            Object.keys(defaultColumnVisibilities).map((columnId) => [
                columnId,
                localStorageState.columnVisibilities[columnId] ?? defaultColumnVisibilities[columnId],
            ])
        )
    );
    const defaultHiddenColumnsIds = useMemo(
        () =>
            Object.entries(defaultColumnVisibilities).flatMap(([columnId, visibility]) =>
                visibility ? [] : [columnId]
            ),
        [defaultColumnVisibilities]
    );
    const [filtersVersion, setFiltersVersion] = useState(0);

    const filterForm = useForm<SearchFieldValues>({
        mode: 'onChange',
        defaultValues: {
            filters: filterSchemas.length ? (location.state?.filters ? location.state.filters : initialFilters) : [],
        },
    });
    const filterQuery = useMemo(
        () => toSpringFilterQuery([...((baseFilters ?? []) as SearchFilterTyped[]), ...filters]),
        [baseFilters, filters]
    );
    const activeFilters = useMemo(() => filters.length, [filters]);
    const { data, isLoading, isError } = useQuery([pageKey, page, pageSize, filterQuery, sorting], ({ signal }) => {
        const baseParams = {
            page: page - 1,
            size: pageSize,
            filter: filterQuery,
            sort: sorting.map((el) => `${el.id},${el.desc ? 'desc' : 'asc'}`.replaceAll('_', '.')),
            config: { signal },
        };

        if (pagedApiCall) {
            return pagedApiCall(baseParams);
        }

        if (pagedApiCallWithParam) {
            return pagedApiCallWithParam.method({
                ...baseParams,
                ...pagedApiCallWithParam.param,
            });
        }

        throw new Error('SearchPage requires pagedApiCall or pagedApiCallWithParam');
    });
    const watchFormFilters = useWatch({ control: filterForm.control, name: 'filters' });

    useEffect(() => {
        if (!embedded) {
            navigate(`${location.pathname}${location.search}`, {
                replace: true,
                state: {
                    ...location.state,
                    filters: watchFormFilters,
                    page: page,
                    pageSize: pageSize,
                    sorting: sorting,
                    view: view,
                    showBulkAction: showBulkAction,
                    selectedIds: showBulkAction
                        ? (selectedRows ?? []).map((row) => (getRowId ? getRowId(row) : row.id))
                        : undefined,
                },
            });
        }
    }, [watchFormFilters, page, pageSize, sorting, view, showBulkAction, selectedRows, navigate, embedded]);

    const listContainerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    const filterFormSchema: TFormElement = useMemo(
        () => ({
            type: UiSchemaType.LAYOUT_VERTICAL,
            elements: [
                {
                    type: UiSchemaType.FIELD_ARRAY,
                    path: 'filters',
                    appendLabel: intl.formatMessage({ id: 'search.filters.add' }),
                    initialValue: {
                        field: null,
                        operator: null,
                        value: null,
                    },
                    element: {
                        type: UiSchemaType.FILTER_GROUP,
                        elements: filterSchemas,
                    },
                },
            ],
        }),
        [intl, filterSchemas]
    );

    useEffect(() => {
        if (data) {
            setTotalPages(data.totalPages || 0);
            setTotalElements(data.totalElements || 0);
        }
    }, [setTotalPages, data?.totalPages, data?.totalElements]);

    const content = useMemo(() => data?.content || ([] as TData[]), [data?.content]);

    const scrollToTop = useCallback(() => {
        const listNode = listContainerRef.current;
        listNode && listNode.scrollIntoView();
    }, [listContainerRef]);

    const handlePageSizeChange = useCallback(
        (event: SelectChangeEvent<number>) => {
            setPageSize(Number(event.target.value));
            setPage(1);
            setExpanded({});
            scrollToTop();
        },
        [setPageSize, setPage, setExpanded, scrollToTop]
    );

    const handleViewChange = useCallback(
        (_: any, nextView: SearchView | null) => {
            if (nextView != null) {
                setView(nextView as SearchView);
            }
        },
        [setView]
    );

    const handleExpandToggle = useCallback(
        (item: TData) => {
            const { id } = item;
            if (expandAll) {
                setExpandAll(false);
                const restExpanded = content.reduce(
                    (acc: { [key: string]: boolean }, el: { id: string | number }) => ({ ...acc, [el.id]: true }),
                    expanded
                );
                setExpanded({ ...restExpanded, [id]: false });
            } else {
                setExpanded({ ...expanded, [id]: !expanded[id] });
            }
        },
        [expandAll, setExpandAll, content, expanded, setExpanded]
    );

    const handleExpandAllToggle = useCallback(() => {
        setExpanded({});
        setExpandAll(!expandAll);
    }, [setExpanded, setExpandAll, expandAll]);

    const handleClose = useCallback(() => setFiltering(false), [setFiltering]);

    const handleSearch = useMemo(
        () =>
            filterForm.handleSubmit((searchData) => {
                const filteredSearchData = sanitizeFilters(searchData?.filters);
                setFilters(filteredSearchData);
                setPage(1);
                setExpanded({});
                filterForm.reset(searchData);
                scrollToTop();
            }),
        [filterForm.handleSubmit, filterSchemas, setFilters, setPage, setExpanded, handleClose, scrollToTop]
    );

    const handleChangePage = useCallback(
        (_: React.ChangeEvent<unknown>, newPage: number) => {
            setPage(newPage);
            scrollToTop();
        },
        [setPage, scrollToTop]
    );

    const handleSavedSearchStore = useCallback(
        (savedSearchId: number | undefined) => {
            setLocalStorageState({
                ...localStorageState,
                savedFilterId: savedSearchId,
            });
        },
        [setLocalStorageState, localStorageState]
    );

    const handleSortingChange: OnChangeFn<SortingState> = useCallback(
        (arg) => {
            setSorting(arg);
            setPage(1);
            setExpanded({});
            scrollToTop();
        },
        [setSorting, setPage, setExpanded, scrollToTop]
    );

    const isExpanded = useCallback((item: TData) => expandAll || expanded[item.id], [expandAll, expanded]);

    const table = useImpactTable({
        data: content,
        columns,
        state: { sorting, columnVisibility: columnVisibilities },
        onColumnVisibilityChange: setColumnVisibilities,
        onSortingChange: handleSortingChange,
    });

    useEffect(() => {
        setLocalStorageState({
            ...localStorageState,
            columnVisibilities: Object.fromEntries(
                Object.entries(columnVisibilities).filter(
                    ([columnId, visibility]) => visibility !== defaultColumnVisibilities[columnId]
                )
            ),
        });
    }, [columnVisibilities]);

    const sortableFields = useMemo(
        () =>
            columns
                .filter((column) => column.enableSorting !== false)
                .map((column) => {
                    const label: string = typeof column.header === 'string' ? column.header : '';
                    return { id: column.id ?? '', label: label };
                }),
        [columns]
    );

    const handleFiltersClick = useCallback(() => setFiltering(!filtering), [setFiltering, filtering]);

    const renderCardItem = useCallback(
        (item: TData) => {
            return CardItem ? CardItem({ item, handleExpandToggle, isExpanded }) : <></>;
        },
        [CardItem, handleExpandToggle, isExpanded]
    );

    const handleResetFilters = useCallback(() => {
        handleSavedSearchStore(undefined);
        filterForm.reset({ filters: [] });
        handleSearch().then(() => setFiltersVersion(filtersVersion + 1));
    }, [filterForm.reset]);

    const handlePredefinedFilterClick = (predefinedFilter: SearchFilter[]) => {
        handleResetFilters();
        const filtersClicked = predefinedFilters?.find((f) => f.filters === predefinedFilter);
        if (!deepEqual(sanitizeFilters(predefinedFilter), filters)) {
            filterForm.setValue('filters', predefinedFilter);
            if (filtersClicked?.sorting) {
                setSorting(filtersClicked.sorting);
            }
        } else {
            if (filtersClicked?.sorting) {
                setSorting(initialSort ? [initialSort] : []);
            }
        }
        handleSearch()
            .then(() => setFiltersVersion(filtersVersion + 1))
            .then(handleClose);
    };

    return (
        <Box
            sx={(theme) => ({
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderBottom: `1px solid ${theme.palette.grey.A200}`,
            })}
        >
            <ImpactHelmet title={title} {...(documentType !== null ? { documentType } : {})} />
            {!hideDefaultHeader || header ? (
                <Box mx={2} my={1} sx={{ display: 'flex', flexDirection: 'column' }}>
                    {header}
                    {!hideDefaultHeader ? (
                        <SearchPageHeader
                            onFiltersClick={handleFiltersClick}
                            onPredefinedFilterClick={handlePredefinedFilterClick}
                            onViewChange={handleViewChange}
                            onExpandAllToggle={handleExpandAllToggle}
                            predefinedFilters={predefinedFilters?.map((predefinedFiltersData) => ({
                                ...predefinedFiltersData,
                                active: deepEqual(sanitizeFilters(predefinedFiltersData.filters), filters),
                            }))}
                            activeFiltersCount={activeFilters}
                            hiddenColumnsIds={defaultHiddenColumnsIds}
                            view={view}
                            isExpandAll={expandAll}
                            table={table}
                            sortableFields={sortableFields}
                            sorting={sorting}
                            onSortingChange={handleSortingChange}
                            hasViewChoice={CardItem !== undefined}
                        />
                    ) : null}
                </Box>
            ) : null}
            <Box
                ref={bodyRef}
                sx={{
                    display: 'flex',
                    overflow: 'auto',
                    flex: 1,
                    flexDirection: 'column',
                }}
            >
                {CardItem !== undefined && view === SearchView.LIST ? (
                    <>
                        <Divider />
                        <Box
                            sx={(theme) => ({
                                py: 2,
                                flex: 1,
                                overflow: 'auto',
                                backgroundColor: theme.palette.grey['100'],
                            })}
                        >
                            <Container
                                maxWidth="md"
                                ref={listContainerRef}
                                sx={{
                                    scrollMargin: (theme) => theme.spacing(2),
                                }}
                            >
                                <Stack spacing={2}>
                                    <SearchCardList
                                        isLoading={isLoading}
                                        isError={isError}
                                        parentRef={bodyRef}
                                        items={data?.content}
                                        itemRender={renderCardItem}
                                    />
                                </Stack>
                            </Container>
                        </Box>
                    </>
                ) : view === SearchView.TABLE ? (
                    <ImpactTable
                        isLoading={isLoading}
                        isError={isError}
                        table={table}
                        selectable={selectable || showBulkAction}
                        selectedRows={selectedRows}
                        onSelect={onSelect}
                        getRowId={getRowId}
                    />
                ) : null}
            </Box>

            <Divider />

            <Box p={1} alignItems="center" display="flex" gap={1}>
                {bulkActions && !isMobile ? (
                    showBulkAction ? (
                        <Box alignItems="center" display="flex" gap={1}>
                            <ResponsiveButton
                                icon={<CloseIcon />}
                                buttonColor="error"
                                buttonVariant="contained"
                                onClick={exitBulkAction}
                            >
                                <FormattedMessage id="common.cancel" />
                            </ResponsiveButton>
                            <Tooltip
                                title={selectedRows?.length === 0 ? <FormattedMessage id="bulk.action.tooltip" /> : ''}
                            >
                                <Select
                                    size="small"
                                    displayEmpty
                                    value={selectedBulkAction}
                                    onChange={(e) => selectBulkAction(e.target.value)}
                                    disabled={selectedRows?.length === 0}
                                    sx={{
                                        background: (theme) => theme.palette.primary.main,
                                        color: 'white',
                                        boxShadow: 2,
                                        height: 30,
                                        transition: (theme) =>
                                            theme.transitions.create(['background-color', 'box-shadow']),
                                        '& .MuiSelect-icon': {
                                            color: 'white',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&:hover': {
                                            background: (theme) => darken(theme.palette.primary.main, 0.2),
                                            boxShadow: 4,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '&.Mui-disabled': {
                                            background: (theme) => theme.palette.action.disabledBackground,
                                            boxShadow: 'none',
                                        },
                                        '&.Mui-disabled .MuiSelect-icon': {
                                            color: (theme) => theme.palette.action.disabled,
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <FormattedMessage id="bulk.action" />
                                    </MenuItem>
                                    {bulkActions.options.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            <FormattedMessage id={bulkActions.translations[option]} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Box>
                            <ResponsiveButton
                                icon={<DoneAll />}
                                buttonVariant="contained"
                                onClick={() => setShowBulkAction(true)}
                            >
                                <FormattedMessage id="bulk.action" />
                            </ResponsiveButton>
                        </Box>
                    )
                ) : null}
                {bulkActionsConfirmation && !isMobile ? (
                    <Box alignItems="center" display="flex" gap={1}>
                        <ResponsiveButton
                            icon={<CloseIcon />}
                            buttonColor={'error'}
                            buttonVariant="contained"
                            onClick={cancelBulkAction}
                        >
                            <FormattedMessage id="common.cancel" />
                        </ResponsiveButton>
                        <ResponsiveButton
                            icon={<Check />}
                            disabled={bulkActionsConfirmation.disabled}
                            onClick={() => bulkActionsConfirmation.onConfirm(selectedRows ?? [])}
                            buttonColor="primary"
                            buttonVariant="contained"
                        >
                            <FormattedMessage
                                id="bulk.action.confirmation"
                                values={{
                                    action: bulkActionsConfirmation.action,
                                    selected: bulkActionsConfirmation.selected,
                                    total: bulkActionsConfirmation.total,
                                }}
                            />
                        </ResponsiveButton>
                    </Box>
                ) : null}
                <Box
                    flex={1}
                    justifyContent="center"
                    alignItems="center"
                    display="flex"
                    gap={1}
                    flexDirection={isMobile ? 'column' : 'row'}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        <FormattedMessage id="search.results.total" values={{ count: totalElements }} />
                    </Typography>
                    <Pagination
                        page={page}
                        onChange={handleChangePage}
                        siblingCount={isMobile ? 0 : 1}
                        boundaryCount={1}
                        count={totalPages}
                        shape="rounded"
                    />
                    {!isMobile ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                <FormattedMessage id="search.pageSize" />
                            </Typography>
                            <Select
                                variant="standard"
                                size="small"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                sx={{
                                    height: 30,
                                    '& .MuiSelect-select:focus': {
                                        backgroundColor: 'transparent',
                                    },
                                    '&::after': {
                                        borderBottomColor: 'transparent',
                                    },
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    ) : null}
                </Box>
                {!bulkActionsConfirmation && selectedCount !== undefined && selectedCount > 0 && !isMobile && (
                    <Stack height={30} direction="row" spacing={1} alignItems="center">
                        <Typography>
                            <FormattedMessage id="search.selected" values={{ number: selectedCount }} />
                        </Typography>
                        {onClearSelection !== undefined && (
                            <IconButton size="small" onClick={onClearSelection}>
                                <Delete />
                            </IconButton>
                        )}
                    </Stack>
                )}
            </Box>
            {savedFilterType ? (
                <FiltersDialog
                    title={filterDialogTitle}
                    open={filtering}
                    form={filterForm}
                    formSchema={filterFormSchema}
                    formGeneratorKey={filtersVersion}
                    savedFilterType={savedFilterType}
                    savedSearchId={localStorageState.savedFilterId}
                    sorting={sorting}
                    setSorting={setSorting}
                    onSaveSearchStore={handleSavedSearchStore}
                    onClose={handleClose}
                    onCancel={handleClose}
                    onUpdateFilters={handleSearch}
                    onResetFilters={handleResetFilters}
                />
            ) : (
                <SimpleFiltersDialog
                    title={filterDialogTitle}
                    open={filtering}
                    form={filterForm}
                    formGeneratorKey={filtersVersion}
                    formSchema={filterFormSchema}
                    onClose={handleClose}
                    onCancel={handleClose}
                    onUpdateFilters={handleSearch}
                    onResetFilters={handleResetFilters}
                />
            )}
        </Box>
    );
};
